// Helpers do turno de caixa (abrir/fechar) — só tela, nada de banco.
import type { CaixaTurno, CaixaTurnoEvento, SangriaTurno } from './types';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Lista de sangrias devolvida pelo turno (banco manda {valor_centavos, observacao, em}).
function normalizarSangrias(v: unknown): SangriaTurno[] {
  if (!Array.isArray(v)) return [];
  return v.map((item) => {
    const o = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    return {
      valor_centavos: num(o.valor_centavos),
      observacao: o.observacao == null ? null : String(o.observacao),
      em: String(o.em ?? ''),
    };
  });
}

// "100", "100,00", "100.00" ou "1.234,56" → centavos. Vazio/ inválido → null.
export function parseDinheiroCentavos(texto: string): number | null {
  const limpo = (texto || '').trim().replace(/\s/g, '');
  if (!limpo) return null;
  let normal = limpo;
  if (normal.includes(',')) {
    normal = normal.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(normal);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

// "14:05" no horário de Brasília.
export function horaBrasilia(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

// Lê o turno devolvido por `caixa_turno_atual` / `caixa_turno_fechar`.
// `` (sem turno) → null. Nunca confia no formato exato.
export function normalizarTurno(v: unknown): CaixaTurno | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const turnoId = Number(o.turno_id);
  if (!Number.isFinite(turnoId) || turnoId <= 0) return null;

  const r = (o.resumo && typeof o.resumo === 'object' ? o.resumo : null) as Record<
    string,
    unknown
  > | null;

  return {
    turno_id: turnoId,
    aberto_em: String(o.aberto_em ?? ''),
    caixa: o.caixa == null ? null : String(o.caixa),
    operador: o.operador == null ? null : String(o.operador),
    troco_inicial_centavos: num(o.troco_inicial_centavos),
    esperado_centavos: num(o.esperado_centavos),
    sangria_centavos: num(o.sangria_centavos),
    sangrias: normalizarSangrias(o.sangrias),
    resumo: r
      ? {
          dinheiro_vendas: num(r.dinheiro_vendas),
          dinheiro_centavos: num(r.dinheiro_centavos),
          pix_centavos: num(r.pix_centavos),
          cartao_centavos: num(r.cartao_centavos),
          desfeitas: num(r.desfeitas),
        }
      : null,
    fechado_em: o.fechado_em == null ? null : String(o.fechado_em),
    contado_centavos: o.contado_centavos == null ? null : num(o.contado_centavos),
    diferenca_centavos: o.diferenca_centavos == null ? null : num(o.diferenca_centavos),
  };
}

// Lê a lista de turnos do evento (sem confiar no formato exato).
export function normalizarTurnosEvento(v: unknown): CaixaTurnoEvento[] {
  if (!Array.isArray(v)) return [];
  return v.map((item) => {
    const o = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    return {
      operador: String(o.operador ?? '—'),
      caixa: o.caixa == null ? null : String(o.caixa),
      aberto_em: o.aberto_em == null ? null : String(o.aberto_em),
      fechado_em: o.fechado_em == null ? null : String(o.fechado_em),
      troco_inicial_centavos: num(o.troco_inicial_centavos),
      esperado_centavos: num(o.esperado_centavos),
      sangria_centavos: num(o.sangria_centavos),
      sangrias: normalizarSangrias(o.sangrias),
      contado_centavos: o.contado_centavos == null ? null : num(o.contado_centavos),
      diferenca_centavos: o.diferenca_centavos == null ? null : num(o.diferenca_centavos),
    };
  });
}